import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Shield } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 20 — Legacy-under-18 blocker.
 *
 * Rendered non-dismissable on ``/student`` when ``/api/auth/me`` returns
 * ``parent_link_required=true``. Blocks the dashboard until the student
 * submits a parent contact via ``POST /api/student/parent-links``.
 * Once submitted, the modal closes; the parent-link status refresh happens
 * on next ``/me`` fetch.
 */
export default function ParentLinkRequiredModal() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.role !== "student" || !user.parent_link_required) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !mobile.trim()) {
      toast.error("Enter both parent email and mobile.");
      return;
    }
    const cleaned = mobile.replace(/[\s-]/g, "");
    if (!/^\+?\d{8,15}$/.test(cleaned)) {
      toast.error("Enter a valid parent mobile (include country code).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/student/parent-links", {
        parent_email: email.trim(),
        parent_mobile: cleaned,
      });
      setSent(true);
      toast.success("Invite sent — your parent will get an email + SMS.");
      // Refresh /me so the UI reflects that a pending link now exists.
      if (refreshUser) await refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail || "add_failed";
      const map = {
        parent_link_already_exists:
          "You already invited this parent — check your existing invitations.",
        parent_mobile_invalid: "That parent mobile number doesn't look valid.",
      };
      toast.error(map[detail] || detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="parent-link-required-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-amber-600" />
            Add your parent to continue
          </DialogTitle>
          <DialogDescription>
            Since you're under 18, we need your parent's email and mobile
            before you can keep using KKS. They'll get a private invite and
            can approve your learning content.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-emerald-900 space-y-2"
            data-testid="parent-link-sent-state"
          >
            <div className="font-medium">Invite on its way</div>
            <p>
              We've queued an email + SMS to <strong>{email}</strong>. Once your
              parent accepts the invite, you'll be able to continue on KKS.
            </p>
            <p className="text-xs text-emerald-800">
              You can close this and come back once they accept — you'll see
              their name in your profile.
            </p>
          </div>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            <div className="space-y-1.5">
              <Label htmlFor="pl-email">Parent email</Label>
              <Input
                id="pl-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="parent@example.com"
                data-testid="parent-link-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-mobile">Parent mobile</Label>
              <Input
                id="pl-mobile"
                type="tel"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91 99998 88877"
                data-testid="parent-link-mobile-input"
              />
              <p className="text-xs text-stone-500">
                Include the country code (e.g. +91, +1).
              </p>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="parent-link-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="mr-1 h-4 w-4" /> Send invite
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
